// src/services/profileService.js
import { db } from '../config/firebase';
import { doc, setDoc, getDoc, updateDoc, deleteDoc, arrayUnion, arrayRemove } from 'firebase/firestore';

// Free: weekly ceiling only. Pro: a 5-hour rolling burst window (generous
// enough that one heavy session never feels capped) PLUS a weekly ceiling
// as an overall backstop — a short window plus a weekly ceiling. Neither
// tier is ever truly unlimited: Pro's numbers are just high enough that
// the cap functions as an abuse/cost backstop, not a felt constraint.
// Browsing/searching Discover itself is never limited — only opening a
// card (AI match analysis) and the AI chat bubble cost against a cap.
export const TIER_LIMITS = {
  free: {
    weekly: { aiAnalysis: 5, aiChatMessages: 20 },
  },
  pro: {
    weekly: { aiAnalysis: 150, aiChatMessages: 400 },
    window: { aiAnalysis: 15, aiChatMessages: 40 },
  },
};

const FIVE_HOURS_MS = 5 * 60 * 60 * 1000;
const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;

// AI usage is measured and enforced on the server (real Gemini tokens, one
// shared allowance for chat and analysis). The server mirrors the result onto
// the profile as aiUsagePercent / aiUsageResetAt; these helpers only read it.
// A percent left over from a finished week is ignored once its reset time has
// passed, so a student is never locked out by a stale number.
export function getAiUsagePercent(kind, profile) {
  const reset = profile?.aiUsageResetAt;
  if (reset && Date.now() >= reset) return 0;
  return Math.max(0, Math.min(100, Math.round(profile?.aiUsagePercent || 0)));
}

function getRemainingUsage(kind, profile, tier) {
  return getAiUsagePercent(kind, profile, tier) >= 100 ? 0 : 1;
}

// Kept so existing callers keep working; the server does the counting now.
async function incrementUsage() {}

export async function setApplicationStatus(uid, profile, oppId, status) {
  const applicationStatus = {
    ...(profile?.applicationStatus || {}),
    [oppId]: { status, updatedAt: new Date().toISOString() },
  };
  await updateDoc(doc(db, 'profiles', uid), { applicationStatus }).catch(() => {});
  return applicationStatus;
}

// Returns the new saved list on success, or null if the write failed, so
// the caller can roll back its optimistic UI instead of showing a save
// that never persisted.
export async function toggleSavedOpportunity(uid, profile, oppId) {
  const current = profile?.savedOpportunities || [];
  const isSaved = current.includes(oppId);
  const next = isSaved ? current.filter(id => id !== oppId) : [...current, oppId];
  try {
    await updateDoc(doc(db, 'profiles', uid), {
      savedOpportunities: isSaved ? arrayRemove(oppId) : arrayUnion(oppId),
    });
    return next;
  } catch (err) {
    console.error('Toggle saved opportunity error:', err);
    return null;
  }
}

export async function saveProfile(uid, profile) {
  try {
    await setDoc(doc(db, 'profiles', uid), {
      ...profile,
      updatedAt: new Date().toISOString(),
    }, { merge: true });
  } catch (err) {
    console.error('Save profile error:', err);
  }
}

// Onboarding must run once per account, at sign-up. The profile document is
// the source of truth (profileSetupComplete, or a username, which is only
// ever stored when the questionnaire was finished), but a failed or delayed
// save must never send a student back to question 1, so this browser also
// remembers completion per account.
const onboardingKey = (uid) => `elpis_onboarded_${uid}`;
export function markOnboardingDone(uid) {
  try { localStorage.setItem(onboardingKey(uid), '1'); } catch { /* storage blocked */ }
}
export function isOnboardingDone(uid, profile) {
  if (profile?.profileSetupComplete || profile?.username) return true;
  try { return localStorage.getItem(onboardingKey(uid)) === '1'; } catch { return false; }
}

// The signup OTP is asked once per account. Like onboarding, this browser
// remembers it so a profile that fails to read or save can never turn every
// page load into a brand-new signup.
const signupVerifiedKey = (uid) => `elpis_signup_verified_${uid}`;
export function markSignupVerified(uid) {
  try { localStorage.setItem(signupVerifiedKey(uid), '1'); } catch { /* storage blocked */ }
}
export function isSignupVerifiedLocal(uid) {
  try { return localStorage.getItem(signupVerifiedKey(uid)) === '1'; } catch { return false; }
}

// Same write as saveProfile, but lets the error through. Onboarding uses it
// so a rejected save is shown to the student instead of silently looping
// them back to question 1 on the next visit.
export async function saveProfileStrict(uid, profile) {
  await setDoc(doc(db, 'profiles', uid), {
    ...profile,
    updatedAt: new Date().toISOString(),
  }, { merge: true });
}

// Real bug fixed here (2026-09-30): this used to swallow ANY Firestore
// error (including a transient permission-denied from onAuthStateChanged
// re-firing before the SDK's auth token was fully attached — a known
// Firebase race, not rare) and return null either way. The caller
// (AuthContext.js) has no way to tell "this account genuinely has no
// profile yet" apart from "the read itself just failed" — both looked
// identical, so a transient error silently demoted a fully-onboarded
// returning user back to a brand-new-user default, wiping
// profileSetupComplete/signupVerified locally and re-triggering onboarding
// + the signup OTP gate. Now throws on a real error so the caller can
// distinguish "no profile" (null) from "couldn't check" (throws) and react
// differently — never returns a fabricated null for both.
export async function loadProfile(uid) {
  const snap = await getDoc(doc(db, 'profiles', uid));
  return snap.exists() ? snap.data() : null;
}

// Was defined but never actually called anywhere in the app — meaning
// weeklyAiAnalysis never reset and a free user who hit 5 stayed capped
// forever, not weekly as the UI ("AI analyses left this week") implies.
// Now wired in from DashboardPage on load. Two independent clocks: the
// weekly one applies to every tier; the 5-hour burst window only ever
// matters for Pro (Free has no `window` limits, so those fields just sit
// unused at 0 for free accounts).
export async function checkAndResetUsageWindows(uid, profile) {
  const now = new Date();
  const updates = {};

  const lastWeekly = profile?.lastWeeklyReset ? new Date(profile.lastWeeklyReset) : null;
  if (!lastWeekly || now - lastWeekly >= SEVEN_DAYS_MS) {
    updates.weeklyAiAnalysis = 0;
    updates.weeklyAiChatMessages = 0;
    updates.lastWeeklyReset = now.toISOString();
  }

  const lastWindow = profile?.lastWindowReset ? new Date(profile.lastWindowReset) : null;
  if (!lastWindow || now - lastWindow >= FIVE_HOURS_MS) {
    updates.windowAiAnalysis = 0;
    updates.windowAiChatMessages = 0;
    updates.lastWindowReset = now.toISOString();
  }

  if (Object.keys(updates).length > 0) {
    await updateDoc(doc(db, 'profiles', uid), updates).catch(() => {});
  }
}

export async function incrementAiAnalysisCount(uid, profile) { return incrementUsage('aiAnalysis', uid, profile); }
export function getRemainingAiAnalysis(profile, tier) { return getRemainingUsage('aiAnalysis', profile, tier); }

// Streak only advances by completing the daily lesson quiz at
// /daily-course — see DailyCoursePage.js. "Just opening the app" doesn't
// count (was the original, too-basic version of this).
//
// Milestone reward every N days — free tier every 7, Pro every 3 (more
// frequent, matching Atharva's call). Rewards alternate between a one-time
// AI-credit top-up and a streak freeze, so it's not a pure cost sink every
// single milestone: odd milestone numbers grant credits, even ones grant a
// freeze. A freeze covers exactly one missed day (same as Duolingo) —
// if the user skips a day but has a freeze banked, the streak continues
// instead of resetting to 1, and the freeze is spent.
//
// Credits are granted as a one-time reduction of the current week's USED
// counters (floored at 0) — not a permanent formula change — so
// getRemainingUsage above needs no changes to reflect it.
const STREAK_INTERVAL = { free: 7, pro: 3 };
const MILESTONE_CREDIT = { aiAnalysis: 1, aiChatMessages: 5 };

// Big, rare, one-time-per-climb milestones — exact-day matches, not a
// repeating modulo like the small weekly ones. Reward scales with how far
// the streak has gone (2/4/10 "credit units", each unit = MILESTONE_CREDIT).
// Deliberately NOT retroactive: if the streak breaks at day 28, that unclaimed
// 30-day tier is gone — reaching 30 again means genuinely doing 30 more
// straight days. The streak number itself can survive a single missed day
// via a freeze, but nothing here hands out a big-tier reward early or backfills
// a missed one; it only ever fires the exact moment nextStreak equals the tier.
const BIG_MILESTONES = { 30: 2, 50: 4, 100: 10 };

function todayKey() { return new Date().toISOString().slice(0, 10); }
function daysAgoKey(n) { return new Date(Date.now() - n * 86400000).toISOString().slice(0, 10); }

// Called from DailyCoursePage.js's finishQuiz — NOT on app open.
export async function updateStreak(uid, profile) {
  const today = todayKey();
  if (profile?.lastActiveDate === today) return null; // already recorded today, no re-grant

  const yesterday = daysAgoKey(1);
  const twoDaysAgo = daysAgoKey(2);
  const freezesAvailable = profile?.streakFreezes || 0;

  let nextStreak;
  let freezeUsed = false;

  if (profile?.lastActiveDate === yesterday) {
    nextStreak = (profile?.streakCount || 0) + 1;
  } else if (profile?.lastActiveDate === twoDaysAgo && freezesAvailable > 0) {
    // Missed exactly one day, but a banked freeze covers it.
    nextStreak = (profile?.streakCount || 0) + 1;
    freezeUsed = true;
  } else {
    nextStreak = 1;
  }

  const updates = {
    streakCount: nextStreak,
    lastActiveDate: today,
  };
  if (freezeUsed) updates.streakFreezes = freezesAvailable - 1;

  // A genuine break — not covered by a freeze, and there was a real streak
  // worth losing (3+ days, not just resetting from a fresh account). How
  // it can be gotten back depends on how long they were actually gone —
  // see RESTORE_TIERS / restoreStreak below.
  const priorStreak = profile?.streakCount || 0;
  if (nextStreak === 1 && priorStreak >= 3 && !freezeUsed) {
    const lastActive = profile?.lastActiveDate ? new Date(profile.lastActiveDate) : null;
    const daysMissed = lastActive ? Math.round((Date.now() - lastActive.getTime()) / 86400000) - 1 : 99;
    updates.lostStreak = {
      value: priorStreak,
      daysMissed,
      expiresAt: new Date(Date.now() + 3 * 86400000).toISOString(),
    };
  }

  // Big tiers take priority over the small recurring one on any day they
  // coincide (e.g. Pro's 3-day interval divides evenly into 30).
  const bigUnits = BIG_MILESTONES[nextStreak];
  let milestoneHit = false;
  let rewardType = null;
  let tierSize = 'small';

  if (bigUnits) {
    milestoneHit = true;
    rewardType = 'credits';
    tierSize = 'big';
    updates.weeklyAiAnalysis = Math.max((profile?.weeklyAiAnalysis || 0) - MILESTONE_CREDIT.aiAnalysis * bigUnits, 0);
    updates.weeklyAiChatMessages = Math.max((profile?.weeklyAiChatMessages || 0) - MILESTONE_CREDIT.aiChatMessages * bigUnits, 0);
  } else {
    const tier = profile?.tier || 'free';
    const interval = STREAK_INTERVAL[tier] || STREAK_INTERVAL.free;
    const milestoneNumber = nextStreak % interval === 0 ? nextStreak / interval : 0;
    milestoneHit = milestoneNumber > 0;
    rewardType = milestoneHit ? (milestoneNumber % 2 === 1 ? 'credits' : 'freeze') : null;

    if (rewardType === 'credits') {
      updates.weeklyAiAnalysis = Math.max((profile?.weeklyAiAnalysis || 0) - MILESTONE_CREDIT.aiAnalysis, 0);
      updates.weeklyAiChatMessages = Math.max((profile?.weeklyAiChatMessages || 0) - MILESTONE_CREDIT.aiChatMessages, 0);
    } else if (rewardType === 'freeze') {
      updates.streakFreezes = (updates.streakFreezes ?? freezesAvailable) + 1;
    }
  }

  await updateDoc(doc(db, 'profiles', uid), updates).catch(() => {});
  return { streakCount: nextStreak, freezeUsed, milestoneHit, rewardType, tierSize, creditUnits: bigUnits || 1, updates };
}

// Lifetime cap — this is meant to feel like a rare grace, not a safety net
// that makes losing a streak consequence-free. Once used up, a broken
// streak is genuinely gone, same as it would be without this feature at all.
export const MAX_STREAK_RESTORES = 3;

// How the restore is priced scales with how long they were actually gone —
// a one-day slip costs nothing, a week+ gap costs real credits AND proof
// of re-engagement (2 fresh lessons), and past 14 days it's just gone.
// Lessons-required scales with how long they were actually gone, rather
// than a flat number — 13 days missed needs real proof (7 lessons), a
// 2-day gap doesn't. Whatever tier lands on credits+lessons also gets a
// 3-day deadline to actually finish the lessons (see pendingRestore.expiresAt
// below) — starting the restore doesn't mean infinite time to complete it.
function restoreTierFor(daysMissed) {
  if (daysMissed <= 1) return { kind: 'free' };
  if (daysMissed <= 6) return { kind: 'credits', analysisCost: 1, chatCost: 5 };
  if (daysMissed <= 13) {
    return { kind: 'credits+lessons', analysisCost: 2, chatCost: 10, lessonsRequired: Math.ceil(daysMissed / 2) };
  }
  // A month-scale gap (exams, travel, whatever real life is) — still
  // recoverable, but cost and proof-of-return scale up a lot. Past 29
  // days it's genuinely gone.
  if (daysMissed <= 29) {
    return { kind: 'credits+lessons', analysisCost: 5, chatCost: 25, lessonsRequired: Math.ceil(daysMissed / 3), isBigGap: true };
  }
  return null; // too long gone — not restorable
}

// Separate, tighter cap on top of MAX_STREAK_RESTORES — specifically for
// the 14-29 day tier: 2 uses per rolling 18-month window (not a lifetime
// cap), tracked via timestamps so old uses actually roll off rather than
// permanently using up the budget. Someone who repeatedly disappears for
// weeks at a time shouldn't be able to buy their way back indefinitely,
// but this isn't meant to be a one-time-ever allowance either.
export const MAX_BIG_GAP_RESTORES = 2;
const BIG_GAP_WINDOW_MS = 18 * 30 * 86400000; // ~18 months

function recentBigGapRestores(profile) {
  const dates = profile?.bigGapRestoreDates || [];
  const cutoff = Date.now() - BIG_GAP_WINDOW_MS;
  return dates.filter(d => new Date(d).getTime() > cutoff);
}

export function getRestoreOption(profile) {
  const lost = profile?.lostStreak;
  if (!lost) return null;
  if ((profile?.streakRestoresUsed || 0) >= MAX_STREAK_RESTORES) return null;
  if (new Date(lost.expiresAt).getTime() <= Date.now()) return null;
  const tier = restoreTierFor(lost.daysMissed ?? 99);
  if (!tier) return null;
  if (tier.isBigGap && recentBigGapRestores(profile).length >= MAX_BIG_GAP_RESTORES) return null;
  // No balance check needed — "cost" here is stored usage counters
  // (weeklyAiAnalysis/weeklyAiChatMessages), applied as extra usage on
  // restore, not a spendable balance that could run out.
  return { ...tier, streakValue: lost.value, daysMissed: lost.daysMissed };
}

export function canRestoreStreak(profile) {
  return getRestoreOption(profile) != null;
}

// free/credits tiers restore immediately. credits+lessons does NOT restore
// here — it starts a pendingRestore that only finalizes once the required
// extra lessons are actually completed (see progressPendingRestore below,
// called from DailyCoursePage.js's finishQuiz).
export async function restoreStreak(uid, profile) {
  const option = getRestoreOption(profile);
  if (!option) return null;

  if (option.kind === 'credits+lessons') {
    const updates = {
      pendingRestore: {
        streakValue: option.streakValue,
        analysisCost: option.analysisCost,
        chatCost: option.chatCost,
        lessonsRequired: option.lessonsRequired,
        lessonsCompleted: 0,
        expiresAt: new Date(Date.now() + 3 * 86400000).toISOString(),
        isBigGap: !!option.isBigGap,
      },
      lostStreak: null,
    };
    await updateDoc(doc(db, 'profiles', uid), updates).catch(() => {});
    return { pending: true, updates };
  }

  const updates = {
    streakCount: option.streakValue,
    lastActiveDate: todayKey(),
    lostStreak: null,
    streakRestoresUsed: (profile?.streakRestoresUsed || 0) + 1,
  };
  if (option.kind === 'credits') {
    updates.weeklyAiAnalysis = (profile?.weeklyAiAnalysis || 0) + option.analysisCost;
    updates.weeklyAiChatMessages = (profile?.weeklyAiChatMessages || 0) + option.chatCost;
  }

  await updateDoc(doc(db, 'profiles', uid), updates).catch(() => {});
  return { streakCount: option.streakValue, updates };
}

// Called from finishQuiz whenever a lesson completes and there's a pending
// credits+lessons restore in progress. Increments the count; once the
// requirement is met, applies the credit cost and finalizes streakCount in
// the SAME write, then clears pendingRestore. If the 3-day window has
// passed, the restore is simply forfeited — no partial credit for lessons
// done so far, same as never having started it.
export async function progressPendingRestore(uid, profile) {
  const pending = profile?.pendingRestore;
  if (!pending) return null;

  if (new Date(pending.expiresAt).getTime() <= Date.now()) {
    await updateDoc(doc(db, 'profiles', uid), { pendingRestore: null }).catch(() => {});
    return { finalized: false, expired: true, updates: { pendingRestore: null } };
  }

  const lessonsCompleted = pending.lessonsCompleted + 1;
  if (lessonsCompleted < pending.lessonsRequired) {
    const updates = { pendingRestore: { ...pending, lessonsCompleted } };
    await updateDoc(doc(db, 'profiles', uid), updates).catch(() => {});
    return { finalized: false, lessonsCompleted, lessonsRequired: pending.lessonsRequired, updates };
  }

  const updates = {
    streakCount: pending.streakValue,
    lastActiveDate: todayKey(),
    pendingRestore: null,
    streakRestoresUsed: (profile?.streakRestoresUsed || 0) + 1,
    weeklyAiAnalysis: (profile?.weeklyAiAnalysis || 0) + pending.analysisCost,
    weeklyAiChatMessages: (profile?.weeklyAiChatMessages || 0) + pending.chatCost,
  };
  if (pending.isBigGap) updates.bigGapRestoreDates = [...recentBigGapRestores(profile), new Date().toISOString()];
  await updateDoc(doc(db, 'profiles', uid), updates).catch(() => {});
  return { finalized: true, streakCount: pending.streakValue, updates };
}

export async function incrementAiChatCount(uid, profile) { return incrementUsage('aiChatMessages', uid, profile); }
export function getRemainingAiChats(profile, tier) { return getRemainingUsage('aiChatMessages', profile, tier); }

// 40 adjectives x 50 nouns = 2,000 possible handles. Usernames are never
// hand-typed — students only ever get one rolled for them from this pool,
// either on signup or by hitting reroll in Settings.
const USERNAME_ADJECTIVES = [
  'swift', 'bright', 'bold', 'keen', 'brave', 'quiet', 'sharp', 'quick',
  'calm', 'sunny', 'steady', 'curious', 'clever', 'daring', 'eager',
  'spry', 'plucky', 'zesty', 'nimble', 'wandering', 'hopeful', 'radiant',
  'stellar', 'cosmic', 'electric', 'golden', 'coral', 'amber', 'dawn',
  'dusky', 'breezy', 'jolly', 'spunky', 'witty', 'gritty', 'fearless',
  'restless', 'dreamy', 'vivid', 'blazing',
];
const USERNAME_NOUNS = [
  'scholar', 'falcon', 'comet', 'maple', 'summit', 'harbor', 'compass',
  'ember', 'orbit', 'meadow', 'quartz', 'ridge', 'lantern', 'voyager',
  'otter', 'sparrow', 'lynx', 'heron', 'willow', 'brook', 'canyon',
  'glacier', 'nova', 'phoenix', 'tiger', 'panda', 'dolphin', 'eagle',
  'wolf', 'fox', 'raven', 'badger', 'juniper', 'cedar', 'birch', 'pine',
  'reef', 'tide', 'delta', 'prairie', 'tundra', 'oasis', 'mesa',
  'plateau', 'spring', 'meridian', 'zenith', 'aurora', 'beacon', 'voyage',
];

function randomUsername() {
  const a = USERNAME_ADJECTIVES[Math.floor(Math.random() * USERNAME_ADJECTIVES.length)];
  const n = USERNAME_NOUNS[Math.floor(Math.random() * USERNAME_NOUNS.length)];
  return `${a}-${n}`;
}

// Real uniqueness, not a hope — checks the usernames/{username} reservation
// doc, not just "does this look free." A signed-in user can read any
// reservation doc (see firestore.rules), so this works for every candidate.
export async function isUsernameAvailable(username) {
  const snap = await getDoc(doc(db, 'usernames', username));
  return !snap.exists();
}

// Reserving is release-old + create-new, never an in-place rename — the
// security rules only allow `create` on a username doc that doesn't exist
// yet, so two people can never end up holding the same handle even if they
// reroll into it at the same moment.
export async function reserveUsername(username, uid) {
  await setDoc(doc(db, 'usernames', username), { uid });
}

export async function releaseUsername(username) {
  if (!username) return;
  await deleteDoc(doc(db, 'usernames', username)).catch(() => {});
}

// Pulls a batch of ACTUALLY-available candidates (checked live, not just
// generated) — used both for the onboarding username-selection step and
// for a single reroll suggestion in Settings. Capped at 40 draws from the
// 2,000-combination pool so a near-exhausted pool can't hang the UI.
export async function generateUsernameCandidates(count = 4) {
  const found = [];
  const seen = new Set();
  let attempts = 0;
  while (found.length < count && attempts < 40) {
    attempts += 1;
    const candidate = randomUsername();
    if (seen.has(candidate)) continue;
    seen.add(candidate);
    if (await isUsernameAvailable(candidate)) {
      found.push(candidate);
    }
  }
  return found;
}

export function calcProfileCompletion(profile) {
  if (!profile) return 0;
  let score = 0;
  if (profile.displayName) score += 20;
  if (profile.grade) score += 15;
  if (profile.bio) score += 15;
  if (profile.interests?.length > 0) score += 20;
  if (profile.skills?.length > 0) score += 15;
  if (profile.goals?.length > 0) score += 15;
  return Math.min(score, 100);
}
